import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Filter, Bell, Camera as CameraIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import { CameraAlertsSection } from '@/components/cameras/CameraAlertsSection'  // CP 163
import { camerasApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import type { Camera } from '@/types'
import { SectionSkeleton, SectionError, SaveStatus } from './SectionState'
import { camerasQuery, adminCamerasQuery } from './prefetch'

// ============================================================================
// Push Notification Settings — per-camera push toggle + class filter
// ============================================================================

function PushCameraCard({ camera }: { camera: Camera }) {
  const { t } = useTranslation('cameras')
  const { t: ts } = useTranslation('settings')
  const queryClient = useQueryClient()
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false)
  const [expanded, setExpanded] = useState(false)
  // Local optimistic state for toggle
  const [enabled, setEnabled] = useState(camera.push_enabled)

  // Sync local state when server data changes
  useEffect(() => {
    setEnabled(camera.push_enabled)
  }, [camera.push_enabled])

  const toggleMutation = useMutation({
    mutationFn: (push_enabled: boolean) =>
      camerasApi.updatePushSettings(camera.id, { push_enabled }),
    onSuccess: (_data, push_enabled) => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      toast.success(push_enabled ? t('push.enabledFor', { name: camera.name }) : t('push.disabledFor', { name: camera.name }))
    },
    onError: () => {
      setEnabled(camera.push_enabled) // revert
      toast.error(t('push.toggleFailed'))
    },
  })

  // CP 163 — the legacy class-list writer is GONE. It read push_class_names (which a
  // category filter derives to []), displayed the camera as "All classes", and replaced
  // push_filters wholesale on save — destroying the owner's category choice. The camera
  // sheet's picker is reused below instead, writing push_filters like everything else.
  const configured = Object.keys(camera.push_filters ?? {}).length > 0
  const summaryText = configured ? ts('push.someCategories') : ts('push.allCategories')

  const handleToggle = () => {
    const next = !enabled
    setEnabled(next) // optimistic
    toggleMutation.mutate(next)
  }

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-center gap-3 p-3">
        <div className={cn(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full',
          enabled
            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'bg-muted text-muted-foreground'
        )}>
          <CameraIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{camera.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {camera.location || camera.imei}
            {' · '}
            {summaryText}
          </p>
          <SaveStatus pending={toggleMutation.isPending} saved={toggleMutation.isSuccess} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"
            title={t('push.configureFilter')}
          >
            <Filter className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={handleToggle}
            disabled={toggleMutation.isPending}
            className={cn(
              'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
              enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'
            )}
          >
            <span className={cn(
              'inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform',
              enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
            )} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t px-3 pb-3 pt-2">
          <CameraAlertsSection camera={camera} isSuperuser={isSuperuser} />
        </div>
      )}
    </div>
  )
}

export function PushNotificationSection() {
  const { t } = useTranslation('cameras')
  const { t: ts } = useTranslation('settings')
  // Item 307 — fetches its own camera list (which one: see the #323 comment below) and shows its frame with
  // placeholder rows while it loads, instead of staying hidden until the list arrives.
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false)
  // #323 — a super admin lists every camera from the admin list (scope=all, #095), which carries push_enabled, push_filters and is_owner since #323; everyone else lists the cameras they own from the app's shared list. The API lets a superuser change push on any camera (CP 159).
  // <Camera[]>: the two lists have different literal keys, so a union of the two options matches no useQuery overload
  const { data: cameras, isPending, isError, refetch } = useQuery<Camera[]>(isSuperuser ? adminCamerasQuery : camerasQuery)
  const listed = isSuperuser ? (cameras ?? []) : (cameras ?? []).filter(c => c.is_owner)
  const enabledCount = listed.filter(c => c.push_enabled).length

  // Loaded, and the user owns no camera: nothing to set here (as before).
  if (cameras && listed.length === 0) return null

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{t('push.title')}</h2>
        <p className="text-xs text-muted-foreground">
          {ts('push.description')}
        </p>
      </div>

      {isPending ? (
        <SectionSkeleton rows={3} />
      ) : isError && !cameras ? (
        <SectionError onRetry={() => refetch()} />
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Bell className="h-3.5 w-3.5" />
            <span>{ts('push.enabledCount', { enabled: enabledCount, total: listed.length })}</span>
          </div>

          <div className="space-y-2">
            {listed.map(camera => (
              <PushCameraCard key={camera.id} camera={camera} />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
